import React, {useContext} from 'react';
import {RouteComponentProps, Redirect, Route, Switch} from 'react-router-dom';
import {SettingDefinition} from 'shared/account-settings/actions/notification-settings';
import NotificationSettingsForm from 'shared/account-settings/containers/notification-settings-form';
import MediaQuery from 'shared/media-query';
import {ACCOUNT_SETTING_ROUTES} from 'src/components/AccountSettings/utils/utils';
import 'stylesheets/components/account-settings-container.scss';
import 'stylesheets/components/account-settings-form-section.scss';
import 'stylesheets/components/account-settings-tab-button.scss';
import 'stylesheets/components/shared-button.scss';
import ChangePasswordForm from '../../../shared/account-settings/components/ChangePasswordForm';
import {useMediaQuery} from '../../hooks/useMediaQuery';
import {PlatformContext} from '../PlatformContext';
import IonLink from '../mobile-link';
import {
  AccountSettingsContextProps,
  AccountSettingsProvider,
  SharedAccountSettings,
  useAccountSettingsContext,
} from './AccountSettingsContext';
import Help from './Help';
import TabBar from './TabBar';
import TabButton from './TabButton';
import styles from './styles.module.scss';

export type AccountSettingsTabComponent<Type extends SharedAccountSettings> =
  RouteComponentProps & {
    setNavigationAnnouncement: (message: string) => void;
    settings: Type;
    saveSettings: (settings: Partial<Type>, previousSettings?: Type | null) => Promise<Type>;
    setSettings: (settings: Type) => void;
  };

export interface Tab<Type extends SharedAccountSettings> {
  label: string;
  path: string;
  component: React.ComponentType<AccountSettingsTabComponent<Type>>;
}

export type AccountSettingsProps<Type extends SharedAccountSettings> =
  AccountSettingsContextProps<Type> & {
    fetchAccountSettings: () => Promise<Type>;
    availableNotificationSettings?: SettingDefinition[];
    tabs: Tab<Type>[];
  };

function Tabs({renderedTabs}: {renderedTabs: {label: string; path: string}[]}) {
  return (
    <TabBar title="Account Settings" showLogout={false}>
      {renderedTabs.map(({label, path}) => (
        <TabButton key={path} label={label} path={path} />
      ))}
    </TabBar>
  );
}

function AccountSettings<Type extends SharedAccountSettings>({
  tabs,
  availableNotificationSettings,
}: AccountSettingsProps<Type>): React.ReactElement | null {
  const {saveSettings, setSettings, settings, setNavigationAnnouncement} =
    useAccountSettingsContext<Type>();
  const mq = useMediaQuery();
  const platformContext = useContext(PlatformContext);

  const renderedTabs = [
    ...tabs.map(({label, path}) => ({label, path})),
    ACCOUNT_SETTING_ROUTES.password,
  ];

  if (availableNotificationSettings) {
    renderedTabs.push(ACCOUNT_SETTING_ROUTES.notifications);
  }

  if (platformContext.isIonic) {
    renderedTabs.push(ACCOUNT_SETTING_ROUTES.help);
  }

  return (
    <div className="account-settings-container">
      {!platformContext.isIonic && <Tabs renderedTabs={renderedTabs} />}
      <div className="account-settings-form-wrapper">
        <div className="account-settings-form-container" role="tabpanel">
          <Switch>
            {tabs.map(({path, component: Component}) => (
              <Route
                key={path}
                path={path}
                render={(props) => (
                  <>
                    {!platformContext.isIonic && (
                      <MediaQuery.MS_AND_DOWN>
                        <div className={styles.title}>
                          <IonLink to="/account-settings">Account Settings Menu</IonLink>
                        </div>
                      </MediaQuery.MS_AND_DOWN>
                    )}
                    <Component
                      {...props}
                      setSettings={(updatedSettings: Type) => setSettings(updatedSettings)}
                      settings={settings}
                      saveSettings={saveSettings}
                      setNavigationAnnouncement={setNavigationAnnouncement}
                    />
                  </>
                )}
              />
            ))}
            <Route path="/account-settings/password" component={ChangePasswordForm} />
            <Route
              path="/account-settings/notifications"
              render={() => (
                <NotificationSettingsForm
                  availableNotificationSettings={availableNotificationSettings}
                  setNavigationAnnouncement={setNavigationAnnouncement}
                  hasEmail={!!settings?.email}
                  hasMobile={!!settings?.mobile}
                />
              )}
            />
            {platformContext.isIonic && <Route path="/account-settings/help" component={Help} />}
            {mq.SM_AND_UP ? (
              <Redirect from="/account-settings" to={tabs[0].path} />
            ) : (
              <Route
                exact
                path="/account-settings"
                render={() => <Tabs renderedTabs={renderedTabs} />}
              />
            )}
          </Switch>
        </div>
      </div>
    </div>
  );
}

export default function AccountSettingsWithProvider<Type extends SharedAccountSettings>(
  props: AccountSettingsProps<Type>
) {
  return (
    <AccountSettingsProvider<Type> {...props}>
      <AccountSettings {...props} />
    </AccountSettingsProvider>
  );
}
